import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError, redirectToCheckout } from "@/lib/api";
import { LOGO_MARK } from "@/lib/brand";
import { track } from "@/lib/analytics";

const AuthCallback = () => {
  const { googleSession, refreshMe } = useAuth();
  const navigate = useNavigate();
  const processed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      navigate("/login", { replace: true });
      return;
    }
    const sessionId = decodeURIComponent(match[1]);
    const plan = localStorage.getItem("bs_google_plan") || "free";
    const inviteToken = localStorage.getItem("bs_invite_token");
    (async () => {
      try {
        if (inviteToken) {
          const { data } = await api.post(`/team/invite/${inviteToken}/accept-google`, {
            session_id: sessionId,
            origin_url: window.location.origin,
          });
          localStorage.setItem("bs_token", data.token);
          localStorage.removeItem("bs_invite_token");
          localStorage.removeItem("bs_google_plan");
          track("sign_up", { method: "invite_google" });
          await refreshMe();
          window.history.replaceState(null, "", window.location.pathname);
          navigate("/app", { replace: true });
          return;
        }
        const data = await googleSession(sessionId, plan);
        localStorage.removeItem("bs_google_plan");
        if (data.is_new) track("sign_up", { plan: data.intended_plan, method: "google" });
        window.history.replaceState(null, "", window.location.pathname);
        if (data.is_new && (data.intended_plan === "growth" || data.intended_plan === "scale")) {
          const { data: co } = await api.post("/billing/checkout", {
            plan: data.intended_plan,
            origin_url: window.location.origin,
          });
          redirectToCheckout(co.url);
          return;
        }
        navigate("/app", { replace: true });
      } catch (err) {
        localStorage.removeItem("bs_invite_token");
        setError(apiError(err.response?.data?.detail) || err.message);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center" data-testid="auth-callback">
      <img src={LOGO_MARK} alt="loading" className="h-12 w-12 object-contain animate-pulse" />
      {!error ? (
        <p className="mt-4 text-slate-500 text-sm" data-testid="auth-callback-status">Signing you in…</p>
      ) : (
        <div className="mt-4 max-w-sm">
          <p className="text-red-600 text-sm" data-testid="auth-callback-error">{error}</p>
          <button
            onClick={() => navigate("/login", { replace: true })}
            className="mt-4 text-blue-600 font-medium text-sm hover:underline"
            data-testid="auth-callback-retry"
          >
            Back to login
          </button>
        </div>
      )}
    </div>
  );
};

export default AuthCallback;
